import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Howl, HowlOptions } from 'howler';
import debounce from 'lodash.debounce';

export enum BgmKey {
  HOME = 'home',
  WATER_MAGIC = 'waterMagic',
  FIVE_SENSES = 'fiveSenses',
  DINOSAUR_WORLD = 'dinosaurWorld',
  NEWTON_OLYMPICS = 'newtonOlympics',
  CITY_BUILDER = 'cityBuilder',
  SPACE_PIRATE = 'spacePirate',
  DETECTIVE = 'detective',
  HUMAN_BODY = 'humanBody',
  AMAZON = 'amazon',
  CAPTAIN_ELECTRIC = 'captainElectric',
  AMAZING_ARCHITECT = 'amazingArchitect',
  SPACE_WORLD = 'spaceWorld',
}

export const soundMap: Record<BgmKey, string> = {
  home: 'assets/audio/Background music/Home/1647058731297-bbl_music_no_vocal.mp3',
  waterMagic: 'assets/audio/Background music/Subject/G1/1-Water Magic.mp3',
  fiveSenses: 'assets/audio/Background music/Subject/G1/2-Five Senses.wav',
  dinosaurWorld:
    'assets/audio/Background music/Subject/G1/3-Dinosaur World.mp3',
  newtonOlympics:
    'assets/audio/Background music/Subject/G1/4-Newton Olympics.mp3',
  cityBuilder: 'assets/audio/Background music/Subject/G1/5-City Builder.mp3',
  spacePirate: 'assets/audio/Background music/Subject/G1/6-Space Pirate.mp3',
  detective:
    'assets/audio/Background music/Subject/G3/1-detective missions.mp3',
  humanBody: 'assets/audio/Background music/Subject/G3/2-Human Body.mp3',
  amazon: 'assets/audio/Background music/Subject/G3/3-amazon explorer.wav',
  captainElectric:
    'assets/audio/Background music/Subject/G3/4-Captain Electric.mp3',
  amazingArchitect:
    'assets/audio/Background music/Subject/G3/5-amazing architect.mp3',
  spaceWorld: 'assets/audio/Background music/Subject/G3/6-space world.mp3',
};

interface Controller {
  play: () => void;
  stop: () => void;
  fadeIn: () => void;
  fadeOut: () => void;
  sound: Howl;
}

export type SoundEffects = {
  play: (key?: BgmKey) => void;
  stop: () => void;
  muted: boolean;
  setMuted: (muted: boolean) => void;
};

export const useSoundEffects = (): SoundEffects => {
  const [currentController, setCurrentController] =
    useState<{ key: BgmKey; controller: Controller }>();

  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (muted) {
      stop();
    } else {
      play();
    }
  }, [muted]);

  const soundId = useRef<number>();

  const getController = (key: BgmKey): Controller => {
    const maxVolume = 1;

    const sound = new Howl({
      src: soundMap[key],
      loop: true,
      volume: maxVolume,
    });

    // For Debugging
    // sound.on('play', (id) => {
    //   console.log('playing ', id, key);
    // });
    // sound.on('stop', (id) => {
    //   console.log('stopping ', id, key);
    // });

    const play = (): void => {
      soundId.current = sound.play();
    };

    const stop = (): void => {
      sound.stop();
    };

    const fadeDuration = 1000;

    const fadeOut = (): void => {
      sound?.once('fade', () => {
        setTimeout(() => {
          stop();
        }, fadeDuration);
      });
      sound?.fade(maxVolume, 0, fadeDuration);
    };

    const fadeIn = (): void => {
      play();
      sound.play();
      sound?.fade(0, maxVolume, fadeDuration);
    };

    return {
      play,
      stop,
      fadeIn,
      fadeOut,
      sound,
    };
  };

  const play = (key?: BgmKey): void => {
    if (!muted) {
      if (key === undefined) {
        if (currentController !== undefined) {
          const controller = getController(currentController.key);
          controller.fadeIn();
          setCurrentController({ key: currentController.key, controller });
        }
      } else if (key !== currentController?.key) {
        if (currentController !== undefined) {
          currentController.controller.fadeOut();
        }

        const controller = getController(key);
        controller.fadeIn();
        setCurrentController({ key, controller });
      }
    }
  };

  const stop = (): void => {
    if (currentController !== undefined) {
      currentController.controller.fadeOut();
    }
  };

  return {
    play: useCallback(debounce(play, 200), [currentController, muted]),
    stop,
    muted,
    setMuted,
  };
};

export const useSound = (
  src: string,
  options?: Omit<HowlOptions, 'src'>,
): [() => void, () => void, Howl] => {
  const sound = useMemo(() => {
    return new Howl({ src, html5: true, preload: true, ...options });
  }, []);

  return [
    (): void => {
      sound.play();
    },
    (): void => {
      sound.stop();
    },
    sound,
  ];
};
